import { alpha } from '@mui/material';
import { ChartSettingsT } from 'business/definitions';
import { DataZoomComponentOption } from 'echarts';
import { formatAxisLabel } from 'helpers/chartHelper';
import { ChartOptionsArgsT } from 'helpers/eChartHelpers';
import { isDate } from 'helpers/helpers';
import { trackEvent } from 'helpers/trackerHelpers';
import { useDebounce } from 'hooks/helpers';
import { useCallback, useEffect } from 'react';
import { useSettingsContext } from 'routes/Explore/components/Contexts/SettingsContext/SettingsContext';
import { useUpdateSettings } from 'routes/Explore/components/Settings/components/ChartSettingsDrawer/hooks/updateSettings';

type DataZoomEvent = { start: number; end: number };

/** Update the data zoom interval in the chart state and in the chart settings store.
 * We use a debounce to improve the UX by avoiding to update the chart too often.
 * @param {ChartSettingsT['dataZoomInterval']} interval - the new data zoom interval
 * @param {(newInterval: ChartSettingsT['dataZoomInterval']) => void} setInterval - the function to update the data zoom interval in the chart state
 */
export const useSetDataZoomInterval = (
    interval: ChartSettingsT['dataZoomInterval'],
    setInterval: (newInterval: ChartSettingsT['dataZoomInterval']) => void,
) => {
    const currentSettings = useSettingsContext().chartSettings;
    const sendEvent = () => {
        trackEvent('/decision/indicator/datazoom-modified');
    };
    const sendEventWithDebounce = useDebounce(sendEvent, 500);

    const updateChartSetings = useUpdateSettings(currentSettings);
    const updateChartSettingsWithDebounced = useDebounce(
        updateChartSetings,
        500,
    );

    useEffect(() => {
        if (interval) {
            updateChartSettingsWithDebounced({
                ...currentSettings,
                dataZoomInterval: interval,
            });
            sendEventWithDebounce();
        }
    }, [interval]);

    return useCallback(
        (dataZoomEvent: DataZoomEvent | { batch: DataZoomEvent[] }) => {
            if ('batch' in dataZoomEvent && dataZoomEvent.batch.length > 0) {
                // Checks for 'inside' data zoom event
                const { start, end } = dataZoomEvent.batch[0];
                setInterval([start, end]);
            } else if ('start' in dataZoomEvent && 'end' in dataZoomEvent) {
                // Checks for 'slider' data zoom event
                const { start, end } = dataZoomEvent;
                setInterval([start, end]);
            }
        },
        [],
    );
};

type DataZoomParams = Pick<
    ChartOptionsArgsT,
    | 'xType'
    | 'xInterval'
    | 'decimalPrecision'
    | 'displayBy'
    | 'xScale'
    | 'areTicksRotated'
    | 'treeValueTranslations'
    | 'dataZoomInterval'
    | 'color'
> & {
    isHorizontal: boolean;
    show: boolean;
};

/** Get the data zoom options for the chart.
 * @param {DataZoomParams} params - the parameters to build the data zoom options
 * @returns {DataZoomComponentOption[]} the data zoom options
 * @see https://echarts.apache.org/en/option.html#dataZoom
 */
export const getDataZoom = ({
    isHorizontal,
    show,
    xType,
    xInterval,
    decimalPrecision,
    displayBy,
    xScale,
    areTicksRotated,
    treeValueTranslations,
    dataZoomInterval,
    color: chartColors,
}: DataZoomParams): DataZoomComponentOption => {
    const color = chartColors[0];

    return {
        type: 'slider',
        show,
        ...(dataZoomInterval && {
            start: dataZoomInterval[0],
            end: dataZoomInterval[1],
        }),
        ...(isHorizontal && { yAxisIndex: 0 }),
        labelFormatter: (value: number, valueStr: string) =>
            formatAxisLabel(
                isDate(xType) && !isHorizontal ? value : valueStr,
                xType,
                decimalPrecision,
                displayBy,
                xInterval,
                xScale,
                areTicksRotated && !isHorizontal,
                treeValueTranslations,
                false,
            ).toString(),
        backgroundColor: alpha(color, 0),
        dataBackground: {
            lineStyle: {
                color,
            },
            areaStyle: {
                color,
            },
        },
        selectedDataBackground: {
            lineStyle: {
                color,
            },
            areaStyle: {
                color,
                opacity: 0.75,
            },
        },
        fillerColor: alpha(color, 0.25),
    };
};
